import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	UserRoles,
	TLanguageIDs,
	IGetDishInfoV2,
	IGetDishRestaurantPropertiesArguments,
	IGetDishRestaurantProperties,
	IGetDishRestaurantPortionsArguments,
	IGetDishRestaurantPortions,
} from 'types';
import { viewDishInitialState, ViewDishContext, TTypeOfModalWindow } from './viewDishContext';
import { viewDishReducer } from './viewDishReducer';
import { ViewDishActionTypes } from './viewDishActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getDishInfoV2, getDishRestaurantProperties, getDishRestaurantPortions, deletePortion } from 'backend/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';

export const ViewDishState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(viewDishReducer, viewDishInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const {
		response: dishFullInfoResponse,
		loading: dishFullInfoLoading,
		refetch: dishFullInfoRefetch,
	} = useFetchData<IGetDishInfoV2, string>(dishID ? getDishInfoV2 : null, dishID || '');

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetDishRestaurantProperties, IGetDishRestaurantPropertiesArguments>(
		state.restaurantIdForRestaurantProps && state.dishFullInfo?.id ? getDishRestaurantProperties : null,
		{
			restaurantId: state.restaurantIdForRestaurantProps || '',
			dishId: state.dishFullInfo?.id || '',
		},
	);

	const {
		response: restaurantPortionsResponse,
		loading: restaurantPortionsLoading,
		refetch: restaurantPortionsRefetch,
	} = useFetchData<IGetDishRestaurantPortions, IGetDishRestaurantPortionsArguments>(
		state.restaurantIdForPortions && state.dishFullInfo?.id ? getDishRestaurantPortions : null,
		{
			restaurantId: state.restaurantIdForPortions || '',
			dishId: state.dishFullInfo?.id || '',
		},
	);

	useNamedHeader(dishFullInfoResponse?.data.name);

	useEffect(() => {
		dishFullInfoLoading || restaurantPropertiesLoading || restaurantPortionsLoading ? showLoader() : hideLoader();
	}, [dishFullInfoLoading, restaurantPropertiesLoading, restaurantPortionsLoading]);

	useEffect(() => {
		if (dishFullInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const accessibleRestaurants = hasAccess
				? dishFullInfoResponse.data.restaurants
				: dishFullInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			const accessiblePortionsRestaurants = hasAccess
				? dishFullInfoResponse.data.restaurantsWithPortions.map((restaurant) => ({
						id: restaurant.id,
						name: restaurant.name || '',
				  }))
				: dishFullInfoResponse.data.restaurantsWithPortions
						.filter((restaurant) => userRestaurantIDs.includes(restaurant.id))
						.map((restaurant) => ({
							id: restaurant.id,
							name: restaurant.name || '',
						}));

			dispatch({
				type: ViewDishActionTypes.SET_DISH_FULL_INFO,
				payload: {
					dishFullInfo: dishFullInfoResponse.data,
					accessibleRestaurants,
					accessiblePortionsRestaurants,
				},
			});
		}
	}, [loggedUser.restaurants, hasAccess, dishFullInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: ViewDishActionTypes.SET_RESTAURANT_PROPS,
				payload: {
					restaurantProps: restaurantPropertiesResponse.data,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (restaurantPortionsResponse) {
			dispatch({
				type: ViewDishActionTypes.SET_RESTAURANT_PORTIONS,
				payload: {
					restaurantPortions: restaurantPortionsResponse.data,
				},
			});
		}
	}, [restaurantPortionsResponse]);

	useEffect(() => {
		if (state.needRefetchData) {
			dishFullInfoRefetch();
			dispatch({
				type: ViewDishActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.restaurantIdForRestaurantProps]);

	useEffect(() => {
		restaurantPortionsRefetch();
	}, [state.restaurantIdForPortions]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: ViewDishActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	useEffect(() => {
		if (state.needRefetchRestaurantPortionsData) {
			restaurantPortionsRefetch();
			dispatch({
				type: ViewDishActionTypes.REFETCH_PORTIONS_DATA,
				payload: {
					needRefetchRestaurantPortionsData: false,
				},
			});
		}
	}, [state.needRefetchRestaurantPortionsData]);

	const deleteSelectedPortion = async () => {
		try {
			showLoader();
			await deletePortion({
				restaurantId: state.restaurantIdForPortions ?? '',
				portionId: state.selectedPortionId,
			});
			dispatch({
				type: ViewDishActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: true,
				},
			});
			dispatch({
				type: ViewDishActionTypes.REFETCH_PORTIONS_DATA,
				payload: {
					needRefetchRestaurantPortionsData: true,
				},
			});
			dispatch({
				type: ViewDishActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: true,
				},
			});
			dispatch({
				type: ViewDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'successDeletingPortion',
				},
			});
		} catch (err) {
			dispatch({
				type: ViewDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'errorDeletingPortion',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: ViewDishActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: ViewDishActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const refetchRestaurantPortionsData = useCallback((value: boolean) => {
		dispatch({
			type: ViewDishActionTypes.REFETCH_PORTIONS_DATA,
			payload: {
				needRefetchRestaurantPortionsData: value,
			},
		});
	}, []);

	const setRestaurantIdForRestaurantProps = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewDishActionTypes.SET_RESTAURANT_ID_FOR_RESTAURANT_PROPS,
				payload: {
					restaurantIdForRestaurantProps: value,
					restaurantProps: value ? state.restaurantProps : null,
				},
			});
		},
		[state.restaurantProps],
	);

	const setRestaurantIdForPortions = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewDishActionTypes.SET_RESTAURANT_ID_FOR_PORTIONS,
				payload: {
					restaurantIdForPortions: value,
					restaurantPortions: value ? state.restaurantPortions : null,
				},
			});
		},
		[state.restaurantPortions],
	);

	const changeSelectedPortionId = useCallback((value: string) => {
		dispatch({
			type: ViewDishActionTypes.CHANGE_SELECTED_PORTION_ID,
			payload: {
				selectedPortionId: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: ViewDishActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ViewDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ViewDishContext.Provider
			value={{
				deleteSelectedPortion,
				refetchData,
				refetchRestPropsData,
				refetchRestaurantPortionsData,
				setRestaurantIdForRestaurantProps,
				setRestaurantIdForPortions,
				changeSelectedPortionId,
				setTranslationTab,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				needRefetchRestaurantPortionsData: state.needRefetchRestaurantPortionsData,
				dishFullInfo: state.dishFullInfo,
				accessibleRestaurants: state.accessibleRestaurants,
				accessiblePortionsRestaurants: state.accessiblePortionsRestaurants,
				restaurantIdForRestaurantProps: state.restaurantIdForRestaurantProps,
				restaurantIdForPortions: state.restaurantIdForPortions,
				selectedPortionId: state.selectedPortionId,
				restaurantProps: state.restaurantProps,
				restaurantPortions: state.restaurantPortions,
				selectedTranslationTab: state.selectedTranslationTab,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ViewDishContext.Provider>
	);
};
